import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getCheckBatches,
  getPendingChecksBySource,
} from '../../endpoints/financePayments';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import type { CheckBatchSummary } from '../../types/financePayments';
import CreateCheckBatchDialog from './CreateCheckBatchDialog';

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const SOURCE_LABELS: Record<string, string> = {
  donations: 'Donations',
  chapter_supplies: 'Chapter supplies',
  elf_loans: 'ELF loans',
  '(unknown)': 'Unknown',
};

const CHECK_BATCH_LIST_QUERY_OPTS = {
  enabled: true,
  retry: 1,
  refetchOnMount: 'always' as const,
};

const PaymentsCheckBatchesPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { data, isLoading } = useApiGet(
    ['finance-payments-batches', page, rowsPerPage],
    () =>
      getCheckBatches({
        page: page + 1,
        page_size: rowsPerPage,
      }),
    CHECK_BATCH_LIST_QUERY_OPTS
  );

  const { data: pendingBySource } = useApiGet(
    ['finance-payments-pending-by-source'],
    getPendingChecksBySource,
    CHECK_BATCH_LIST_QUERY_OPTS
  );

  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  const columns: ServerSideTableColumn<CheckBatchSummary>[] = [
    {
      id: 'batch_number',
      label: 'Batch',
      width: '22%',
      firstCell: true,
      sortable: false,
      render: (_v, row) => row.batch_number,
    },
    {
      id: 'status',
      label: 'Status',
      width: '12%',
      sortable: false,
      render: (_v, row) => row.status,
    },
    {
      id: 'check_count',
      label: 'Checks',
      width: '10%',
      sortable: false,
      render: (_v, row) => String(row.check_count),
    },
    {
      id: 'total_amount',
      label: 'Total',
      width: '12%',
      sortable: false,
      render: (_v, row) => fmtMoney(row.total_amount),
    },
    {
      id: 'created_at',
      label: 'Created',
      width: '18%',
      sortable: false,
      render: (_v, row) =>
        row.created_at ? new Date(row.created_at).toLocaleString() : '—',
    },
  ];

  const snap = pendingBySource;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Open a batch, add pending checks, finalize when ready to deposit, and
          print the slip as often as needed.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          New batch
        </Button>
      </Box>

      {snap && snap.total > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Unbatched pending checks (by source)
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Same grouping as the deposit pick list: source is the prefix before
            “:” in check_source, or the full value if there is no memo.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1,
              alignItems: 'center',
            }}
          >
            {snap.items.map((row) => (
              <Chip
                key={row.source}
                size="small"
                label={`${SOURCE_LABELS[row.source] ?? row.source}: ${row.count}`}
                variant="outlined"
              />
            ))}
            <Chip
              size="small"
              color="primary"
              label={`Total pending: ${snap.total}`}
            />
          </Box>
        </Paper>
      )}

      {snap !== undefined && snap.total === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No unbatched pending checks in the queue.
        </Typography>
      )}

      <ServerSideTable<CheckBatchSummary>
        data={rows}
        columns={columns}
        loading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={pagination?.total_count ?? 0}
        totalPages={pagination?.total_pages ?? 0}
        hasNext={pagination?.has_next ?? false}
        hasPrev={pagination?.has_prev ?? false}
        order="desc"
        orderBy="created_at"
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
        onSort={() => {}}
        onRowClick={(row) =>
          navigate(`/finance/payments/check-batches/${row.id}`)
        }
        emptyMessage="No batches yet. Create one to start deposit prep."
      />

      <CreateCheckBatchDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={(res) => {
          if (res?.batch?.id) {
            navigate(`/finance/payments/check-batches/${res.batch.id}`);
          }
        }}
      />
    </Box>
  );
};

export default PaymentsCheckBatchesPage;
